import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';

import useControlled from '@ecp/common/src/hooks/useControlled';
import { positionPropTypes, sizePropTypes } from '@ecp/common/src/style/propTypes';
import { overrideProperties } from '@ecp/common/src/style/utils';
//import theme from '@ecp/common/src/style/theme/default';
import moTheme from '@ecp/common/src/style/theme/mo';
import { FlexBox } from '@ecp/common/src/layouts/flex/styled';
import CheckboxUnstyled from '@ecp/common/src/components/unstyled/checkbox/CheckboxUnstyled';
import FavoriteCheckBox from '@ecp/common/src/components/checkbox/mo/FavoriteCheckBox';
import Badge from '@ecp/common/src/components/badge/mo/Badge';
import { BADGE_TYPE } from '@ecp/common/src/const/good';
import CartIcon from '@ecp/common/src/assets/icon/icon_good-card-cart.svg';
import { getIsLogin } from '@mo-recoil/common/user/selector';

const DefaultBadgeRoot = styled(FlexBox)`
  position: relative;
  margin: 0;
  padding: 0;
  ${overrideProperties(sizePropTypes, {})};
`;

const DecoratorRoot = styled(FlexBox)`
  position: absolute;
  justify-content: center;
  ${overrideProperties(positionPropTypes, {})};
  ${overrideProperties(sizePropTypes, {})};
`;

const CornerDecorator = styled(DecoratorRoot)`
  border-radius: 6px 0 6px;
  color: ${(props) => props.theme.color.text.white};
  font-size: ${moTheme.font.size.$14};
  font-weight: ${moTheme.font.weight.bold};
  width: ${({ width }) => width};
`;

const LargeDecorator = styled(CornerDecorator)`
  background: ${(props) => props.theme.color.primary};
  font-size: ${({ badgeContent }) =>
    ['BEST'].includes(badgeContent.toString().toUpperCase()) ? moTheme.font.size.$12 : moTheme.font.size.$14};
  width: ${({ badgeContent, width }) =>
    ['NEW', 'BEST'].includes(badgeContent.toString().toUpperCase()) ? '40px' : width};
`;

const SmallDecorator = styled(CornerDecorator)`
  background: ${({ badgeContent }) =>
    typeof badgeContent === 'number' && badgeContent > 3
      ? (props) => props.theme.color.third
      : (props) => props.theme.color.primary};
  font-size: ${moTheme.font.size.$12};
`;

const PercentDecorator = styled(CornerDecorator)`
  background: ${(props) => props.theme.color.primary};
  &::after {
    content: '%';
    font-size: ${moTheme.font.size.$10};
    font-weight: ${moTheme.font.weight.bold};
    padding-top: 3px;
  }
`;

const RecentDecorator = styled(DecoratorRoot)`
  border-radius: 0 4px;
  background: ${(props) => props.theme.color.background.basic};
  color: ${(props) => props.theme.color.text.basic};
  border: 1px solid ${(props) => props.theme.color.line.background};
  border-top: none;
  border-right: none;
  font-size: ${moTheme.font.size.$12};
  font-weight: ${moTheme.font.weight.medium};
`;

const FavoriteDecorator = ({ badgeContent: valueProp, onChange, ...badgeProps }) => {
  const [value, setValue] = useState(valueProp);
  const login = useRecoilValue(getIsLogin);

  useEffect(() => setValue(valueProp), [valueProp]);

  const handleChange = (e) => {
    e.stopPropagation();
    onChange?.(e.target.checked);
    if (login) setValue(e.target.checked);
  };
  const randomComponentId = (id) => {
    return id.concat(Math.floor(Math.random() * (9999 - 1) + 1));
  };

  return (
    <DecoratorRoot {...badgeProps}>
      <FavoriteCheckBox
        id={randomComponentId('wish')}
        checked={value}
        onChange={handleChange}
        title={badgeProps.title}
      />
    </DecoratorRoot>
  );
};
FavoriteDecorator.propTypes = {
  badgeContent: PropTypes.bool,
  onChange: PropTypes.func,
};

// eslint-disable-next-line no-unused-vars
const CheckboxDecorator = ({ title, badgeContent: valueProp, onChange, ...badgeProps }) => {
  const { value, setValueWhenUncontrolled } = useControlled({ value: valueProp, defaultValue: false });
  const handleChange = useCallback(
    (event) => {
      setValueWhenUncontrolled(event.target.checked);
      onChange && onChange(event.target.checked);
      event.stopPropagation();
    },
    [onChange, setValueWhenUncontrolled]
  );
  const randomComponentId = (id) => {
    return id.concat(Math.floor(Math.random() * (9999 - 1) + 1));
  };

  const id=randomComponentId('check');

  return (
    <DecoratorRoot {...badgeProps}>
      <CheckboxUnstyled
        id={id}
        rootProps={{ htmlFor: id }}
        imageProps={{ width: '22px', height: '22px' }}
        checkedProp={value}
        checked={value}
        value={value}
        onChange={handleChange}
        title={title}
      />
    </DecoratorRoot>
  );
};
CheckboxDecorator.propTypes = {
  badgeContent: PropTypes.bool,
  onChange: PropTypes.func,
  title: PropTypes.string,
};

const CartButton = styled.button`
  width: 35px;
  height: 35px;
  position: absolute;
  display: inline-block;
  background: url(${CartIcon}) no-repeat 20%;
  background-size: 35px 35px;
`;

const CartDecorator = ({ onChange, ...badgeProps }) => {
  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onChange();
    },
    [onChange]
  );
  
  const randomComponentId = (id) => {
    return id.concat(Math.floor(Math.random() * (9999 - 1) + 1));
  };

  return (
    <DecoratorRoot {...badgeProps}>
      <CartButton id={randomComponentId('cart')} onClick={handleClick}/>
    </DecoratorRoot>
  );
};
CartDecorator.propTypes = {
  onChange: PropTypes.func,
};


const GoodBadge = React.forwardRef(({ type, imageWidth: size, rootProps, ...props }, ref) => {
  let sizeProps = {};
  let positionProps = {};
  let otherProps = {};
  let badgeComponent;

  switch (type) {
    case BADGE_TYPE.LARGE:
      sizeProps = { width: '30px', height: '30px' };
      positionProps = { top: 0, left: 0 };
      badgeComponent = LargeDecorator;
      break;
    case BADGE_TYPE.PERCENT:
      sizeProps = { width: '30px', height: '30px' };
      positionProps = { top: 0, left: 0 };
      badgeComponent = PercentDecorator;
      otherProps = { max: 100 };
      break;
    case BADGE_TYPE.SMALL:
      sizeProps = { width: '20px', height: '20px' };
      positionProps = { top: 0, left: 0 };
      badgeComponent = SmallDecorator;
      break;
    case BADGE_TYPE.CART:
      sizeProps = { width: '28px', height: '28px' };
      positionProps = { bottom: '3px', right: '4px' }
      badgeComponent = CartDecorator;
      break;
    case BADGE_TYPE.FAVORITE:
      sizeProps = { width: '16px', height: '14' };
      positionProps = { bottom: '8px', right: '8px' };
      badgeComponent = FavoriteDecorator;
      break;
    case BADGE_TYPE.CHECKBOX:
      sizeProps = { width: '22px', height: '22px' };
      positionProps = { top: '4px', left: '4px' };
      badgeComponent = CheckboxDecorator;
      break;
    case BADGE_TYPE.RECENT:
      sizeProps = { width: '26px', height: '18px' };
      positionProps = { top: '1px', right: '1px' };
      badgeComponent = RecentDecorator;
      break;
  }

  const newProps = {
    ...otherProps,
    badgeComponent,
    size: sizeProps,
    position: positionProps,
    title: props?.title,
  };

  return <Badge {...props} rootProps={{ width: size, height: size, ...rootProps }} {...newProps} ref={ref} />;
});

GoodBadge.displayName = 'GoodBadge';

GoodBadge.propTypes = {
  type: PropTypes.string,
  imageWidth: PropTypes.string,
  badgeContent: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
  rootProps: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  title: PropTypes.string,
};
GoodBadge.defaultProps = {
  rootComponent: DefaultBadgeRoot,
};

export default GoodBadge;
